/**
 * Clearline redesign of the property case-details page — read-only Overview
 * only (no Approve/Documents/POI: those endpoints don't exist for property
 * yet). Field mapping verified against a real POST /vehicle response — see
 * services/adapters/propertyAssessment.js. AI Summary is GET /get_summary on
 * this same property API — see propertyApiService.js.
 */
import { Link, useLocation, useParams } from "react-router-dom";
import { countryFromSearch } from "../utils/caseHref";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronDown, Building2, AlertTriangle, Sparkles, Star, Download, Hash, Globe, Clock, Inbox } from "lucide-react";
import MetaItem from "./components/MetaItem";
import { decodeRefNum } from "../utils/refNumCrypto";
import {
  getPropertyAssessmentDetail,
  getPropertyVehicle,
  getPropertyMediaBaseUrl,
} from "../services/propertyApiService";
import {
  toPropertyOverview,
  groupPropertyImagesBySide,
  buildPropertyImageUrl,
  resolveSummaryEvidenceUrls,
  isUnobservedValue,
} from "../services/adapters/propertyAssessment";
import { usePropertyAiSummary } from "../hooks/usePropertyAiSummary";
import { usePropertyDownloadReport } from "../hooks/usePropertyDownloadReport";
import { getStatusInfo, getClearlineStatusChip, formatClearlineDateTime, formatFullDateTime } from "../utils/statusMapping";
import { usePublishCammyPageSummary } from "../context/CammySpeechContext";
import { formatUploadDuration } from "../utils/uploadDuration";
import CustomMap from "../components/CustomMap";
import ImageLightbox from "./components/ImageLightbox";
import latviaFlag from "../assets/icons/cases/latvia-flag.svg";
import lithuaniaFlag from "../assets/icons/cases/lithuania-flag.svg";
import estoniaFlag from "../assets/icons/cases/estonia-flag.svg";

const COUNTRY_FLAGS = {
  latvia: latviaFlag,
  lv: latviaFlag,
  lat: latviaFlag,
  lithuania: lithuaniaFlag,
  lt: lithuaniaFlag,
  ltu: lithuaniaFlag,
  estonia: estoniaFlag,
  ee: estoniaFlag,
  est: estoniaFlag,
};

const getCountryFlagSrc = (countryName) =>
  COUNTRY_FLAGS[String(countryName || "").toLowerCase().trim()] || "";

const IMAGE_SECTIONS = [
  { key: "exterior", label: "Exterior Images" },
  { key: "interior", label: "Interior Images" },
  { key: "additional", label: "Additional Images" },
];

const SEVERITY_CHIPS = {
  minor: { bg: "#fef3c7", fg: "#92400e" },
  moderate: { bg: "#fed7aa", fg: "#9a3412" },
  major: { bg: "#fecaca", fg: "#991b1b" },
};

const CONDITION_CHIPS = {
  excellent: { bg: "#dcfce7", fg: "#166534" },
  good: { bg: "#dcfce7", fg: "#166534" },
  fair: { bg: "#fef9c3", fg: "#854d0e" },
  poor: { bg: "#fed7aa", fg: "#9a3412" },
  severe: { bg: "#fecaca", fg: "#991b1b" },
};

const formatEnumLabel = (value) => {
  if (!value) return "";
  return String(value)
    .split("_")
    .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
    .join(" ");
};

function StatTile({ label, value }) {
  return (
    <div className="rounded-card border border-cl-card bg-cl-surface p-4 text-center">
      <div className="text-[20px] font-bold leading-tight text-cl-text">{value}</div>
      <div className="mt-1 text-[11px] font-medium text-cl-muted">{label}</div>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div>
      <dt className="text-[10px] font-semibold uppercase tracking-[0.06em] text-cl-label">{label}</dt>
      <dd className="mt-1 text-[13px] text-cl-text">{value || "—"}</dd>
    </div>
  );
}

// user_rating is 0-5. Out-of-range values still render — clamped, not
// hidden — so a future 0 or a surprising 6 from the API is visible as a
// star count rather than silently dropped.
function StarRating({ rating }) {
  const filled = Math.max(0, Math.min(5, Math.round(rating)));
  return (
    <div className="flex items-center gap-1" role="img" aria-label={`${filled} out of 5 stars`}>
      {Array.from({ length: 5 }, (_, i) => (
        <Star
          key={i}
          className={`h-4 w-4 ${i < filled ? "fill-[#f5b429] text-[#f5b429]" : "text-cl-card"}`}
        />
      ))}
    </div>
  );
}

function Chip({ label, colors }) {
  if (!label) return null;
  const c = colors || { bg: "#eef0f3", fg: "#5b6572" };
  return (
    <span
      className="inline-flex items-center rounded-[6px] px-2 py-1 text-[11px] font-semibold"
      style={{ background: c.bg, color: c.fg }}
    >
      {formatEnumLabel(label)}
    </span>
  );
}

function EvidenceThumbs({ indices, images, mediaBaseUrl }) {
  const urls = resolveSummaryEvidenceUrls(indices, images, mediaBaseUrl);
  if (urls.length === 0) return null;
  return (
    <div className="mt-2 flex gap-1">
      {urls.map((url, i) => (
        <a key={i} href={url} target="_blank" rel="noreferrer" className="block h-10 w-10 overflow-hidden rounded-[4px] bg-cl-bg">
          <img alt={`Evidence ${i + 1}`} className="h-full w-full object-cover" src={url} />
        </a>
      ))}
    </div>
  );
}

const STAT_TONES = {
  good: "text-green-700",
  bad: "text-red-700",
  neutral: "text-cl-text",
};

// A stat tile styled after the old TriNetra "Property Image Summary" page —
// label on top, value below — used for the report's header row. `tone`
// colors the value for the two cards where that reading matters at a glance
// (Overall Condition, Risk Flags); every other card stays neutral.
function SummaryStatCard({ label, value, sub, tone = "neutral" }) {
  return (
    <div className="rounded-card border border-cl-card bg-cl-surface p-4">
      <div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-cl-label">{label}</div>
      <div className={`mt-1.5 text-[13px] font-semibold ${STAT_TONES[tone] || STAT_TONES.neutral}`}>{value || "—"}</div>
      {sub && <div className="mt-0.5 text-[11px] text-cl-muted">{sub}</div>}
    </div>
  );
}

// The model's own construction/condition narration arrives as one long
// semicolon-joined string (dozens of short observations run together), which
// reads as an unreadable wall of text on one line. Splitting on "; " turns
// it back into the bullet list it actually is, collapsed behind "Show N
// more" the same way the old TriNetra page does — the underlying data is
// identical, this just stops presenting it worse than the source justifies.
function BulletList({ text, limit = 6 }) {
  const [expanded, setExpanded] = useState(false);
  const items = String(text)
    .split(/;\s*/)
    .map((s) => s.trim())
    .filter(Boolean);

  if (items.length <= 1) {
    return <p className="text-[13px] text-cl-text">{items[0] || text}</p>;
  }

  const shown = expanded ? items : items.slice(0, limit);
  const remaining = items.length - shown.length;

  return (
    <div>
      <ul className="list-inside list-disc space-y-1 text-[13px] text-cl-text">
        {shown.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
      {remaining > 0 && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="mt-2 text-[11.5px] font-semibold text-cl-accent hover:underline"
        >
          Show {remaining} more
        </button>
      )}
      {expanded && items.length > limit && (
        <button
          type="button"
          onClick={() => setExpanded(false)}
          className="ml-3 mt-2 text-[11.5px] font-semibold text-cl-muted hover:underline"
        >
          Show less
        </button>
      )}
    </div>
  );
}

// result.construction is an open bag of fields the model fills in as it can
// (wall_material, roof_material, stories, ...) — rendered generically rather
// than one DetailRow per named field, so a field the model starts/stops
// sending doesn't need a code change here. Short values (a story count, an
// era estimate) read fine as a label/value pair; long ones (the semicolon-
// joined narrations) get their own bulleted subsection instead.
function ConstructionPanel({ construction }) {
  const allEntries = Object.entries(construction || {}).filter(
    ([, v]) => v !== null && v !== undefined && v !== "",
  );
  // Fields the model answered "not shown"/"unclear" for aren't facts about
  // the building — they're the model declining to guess. Listing "Wall
  // Material: not shown" as its own labeled row reads as data when it isn't
  // any, so those fields are dropped rather than displayed.
  const entries = allEntries.filter(([, v]) => !isUnobservedValue(v));

  if (allEntries.length === 0) {
    return <p className="text-[12.5px] text-cl-muted">No construction details available.</p>;
  }
  if (entries.length === 0) {
    return (
      <p className="text-[12.5px] text-cl-muted">
        No construction details were visible in the submitted photos.
      </p>
    );
  }

  const isLong = (v) => typeof v === "string" && (v.includes(";") || v.length > 60);
  const shortEntries = entries.filter(([, v]) => !isLong(v));
  const longEntries = entries.filter(([, v]) => isLong(v));

  return (
    <div className="space-y-4">
      {shortEntries.length > 0 && (
        <dl className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
          {shortEntries.map(([key, value]) => (
            <DetailRow
              key={key}
              label={formatEnumLabel(key)}
              value={typeof value === "boolean" ? (value ? "Yes" : "No") : formatEnumLabel(String(value)) || String(value)}
            />
          ))}
        </dl>
      )}
      {longEntries.map(([key, value]) => (
        <div key={key}>
          <h4 className="mb-1 text-[13px] font-semibold text-cl-text">{formatEnumLabel(key)}</h4>
          <BulletList text={value} />
        </div>
      ))}
    </div>
  );
}

// The Systems table from the old page: one row per building system, a
// checkmark for whether the photos actually showed it, evidence thumbnails
// when they did. condition/observations render inline when the model
// provides them, so this degrades gracefully to a plain seen/not-seen list
// when it doesn't (as on a small photo set).
function SystemsPanel({ systems, images, mediaBaseUrl }) {
  const entries = Object.entries(systems || {});
  if (entries.length === 0) {
    return <p className="text-[12.5px] text-cl-muted">No system details available.</p>;
  }
  // A truthy condition/observations string is not the same as an observed
  // one — the model fills both in with phrasing like "not shown" when the
  // photos didn't cover a system, which used to read as "seen" (any
  // non-empty string) and drew a green checkmark on a system nobody
  // photographed.
  const wasSeen = (sys) =>
    Boolean(sys?.evidence_images?.length) ||
    (Boolean(sys?.condition) && !isUnobservedValue(sys.condition)) ||
    (Boolean(sys?.observations) && !isUnobservedValue(sys.observations));
  const anySeen = entries.some(([, sys]) => wasSeen(sys));

  return (
    <div>
      <div className="divide-y divide-cl-head">
        {entries.map(([key, sys]) => {
          const seen = wasSeen(sys);
          const conditionChip = sys?.condition && !isUnobservedValue(sys.condition) ? sys.condition : null;
          // "Not shown in the uploaded photos" already says this system
          // wasn't seen — repeating it as an observations line under an
          // unrelated system name adds nothing.
          const observations =
            sys?.observations && !isUnobservedValue(sys.observations) ? sys.observations : null;
          return (
            <div key={key} className="flex items-start justify-between gap-3 py-2.5">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[13px] font-medium text-cl-text">{formatEnumLabel(key)}</span>
                  {conditionChip && <Chip label={conditionChip} colors={CONDITION_CHIPS[conditionChip]} />}
                </div>
                {observations && <p className="mt-0.5 text-[11.5px] text-cl-muted">{observations}</p>}
                <EvidenceThumbs indices={sys?.evidence_images} images={images} mediaBaseUrl={mediaBaseUrl} />
              </div>
              <span
                className={`mt-0.5 shrink-0 text-[15px] leading-none ${seen ? "text-green-600" : "text-cl-faint"}`}
                aria-label={seen ? "Seen in photos" : "Not seen in photos"}
              >
                {seen ? "✓" : "✗"}
              </span>
            </div>
          );
        })}
      </div>
      {!anySeen && (
        <p className="mt-3 text-[11.5px] text-cl-muted">
          None of the building systems were visible in the photos.
        </p>
      )}
    </div>
  );
}

const PRIORITY_CHIPS = {
  high: { bg: "#fee2e2", fg: "#991b1b" },
  normal: { bg: "#eef0f3", fg: "#5b6572" },
  low: { bg: "#eef0f3", fg: "#5b6572" },
};

const CHECKLIST_STATUS_CHIPS = {
  present: CONDITION_CHIPS.good,
  absent: SEVERITY_CHIPS.major,
  unclear: { bg: "#eef0f3", fg: "#5b6572" },
  not_assessed: { bg: "#eef0f3", fg: "#5b6572" },
};

// The region-specific checklist (fire/electrical/water/etc. items relevant
// to where the property is) — same table the old portal shows under "Region
// Checklist — <region name>".
function RegionChecklistTable({ checklist, regionName, images, mediaBaseUrl }) {
  if (!checklist?.length) return null;
  return (
    <div>
      <h3 className="mb-3 text-[13px] font-semibold text-cl-text">
        Region Checklist{regionName ? ` — ${regionName}` : ""}
      </h3>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-[12.5px]">
          <thead>
            <tr className="border-b border-cl-head text-left text-[10px] uppercase tracking-[0.06em] text-cl-label">
              <th className="py-2 pr-3 font-semibold">Item</th>
              <th className="py-2 pr-3 font-semibold">Category</th>
              <th className="py-2 pr-3 font-semibold">Priority</th>
              <th className="py-2 pr-3 font-semibold">Status</th>
              <th className="py-2 font-semibold">Evidence</th>
            </tr>
          </thead>
          <tbody>
            {checklist.map((c, i) => (
              <tr key={c.key || i} className="border-b border-cl-head last:border-0">
                <td className="whitespace-nowrap py-2.5 pr-3 text-cl-text">{c.label}</td>
                <td className="py-2.5 pr-3 text-cl-muted">{formatEnumLabel(c.category)}</td>
                <td className="py-2.5 pr-3"><Chip label={c.priority} colors={PRIORITY_CHIPS[c.priority]} /></td>
                <td className="py-2.5 pr-3"><Chip label={c.status} colors={CHECKLIST_STATUS_CHIPS[c.status]} /></td>
                <td className="py-2.5">
                  <EvidenceThumbs indices={c.evidence_images} images={images} mediaBaseUrl={mediaBaseUrl} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// The thumbnail strip the old page calls "Image Inventory" — every photo the
// analysis looked at, one-line captioned with what the model saw in it.
// image_inventory's `image` field is the same 1-based index evidence_images
// uses, resolved against the summary's own images list (not the case's full
// upload list — see resolveSummaryEvidenceUrls).
function ImageInventoryStrip({ inventory, images, mediaBaseUrl }) {
  if (!inventory?.length) return null;
  const byIndex = new Map((images || []).map((img) => [img?.index, img]));
  return (
    <div>
      <h3 className="mb-3 text-[13px] font-semibold text-cl-text">Image Inventory</h3>
      <div className="flex gap-3 overflow-x-auto pb-1">
        {inventory.map((entry) => {
          const url = buildPropertyImageUrl(mediaBaseUrl, byIndex.get(entry.image)?.picture);
          return (
            <a
              key={entry.image}
              href={url || undefined}
              target="_blank"
              rel="noreferrer"
              title={entry.label}
              className="w-24 shrink-0"
            >
              <div className="h-24 w-24 overflow-hidden rounded-[8px] bg-cl-bg">
                {url && (
                  <img alt={entry.label || `Image ${entry.image}`} className="h-full w-full object-cover" src={url} />
                )}
              </div>
              <p className="mt-1 line-clamp-2 text-[11px] text-cl-muted">{entry.label}</p>
            </a>
          );
        })}
      </div>
    </div>
  );
}

// Renders GET /get_summary's result — see propertyApiService.js for the
// endpoint. images (the summary's own image list) resolves evidence_images
// indices via resolveSummaryEvidenceUrls. Laid out to match the old
// ergobalticproperty portal's "Property Image Summary" page: a stat-card
// header, Construction/Systems side by side, the region checklist table,
// then the image inventory strip.
const CONDITION_TONE = {
  excellent: "good",
  good: "good",
  fair: "neutral",
  poor: "bad",
  severe: "bad",
};

// A single number an underwriter can act on without reading the whole
// report: high-priority region-checklist items that aren't confirmed
// present (missing smoke detectors, unclear electrical, etc.), plus any
// visible damage or named risk factor. The job id and raw timestamp the
// report used to lead with tell a reviewer nothing about the property
// itself — this does.
function countRiskFlags(result) {
  const highPriorityGaps = (result?.checklist || []).filter(
    (c) => c?.priority === "high" && c?.status !== "present",
  ).length;
  const damageCount = result?.visible_damages?.length || 0;
  const riskFactorCount = result?.risk_factors?.length || 0;
  return {
    total: highPriorityGaps + damageCount + riskFactorCount,
    highPriorityGaps,
    damageCount,
    riskFactorCount,
  };
}

function AiSummaryResult({ result, images, completedAt, mediaBaseUrl, onRegenerate }) {
  const riskFlags = countRiskFlags(result);
  const riskSub = riskFlags.total === 0
    ? "No issues raised"
    : [
      riskFlags.highPriorityGaps ? `${riskFlags.highPriorityGaps} high-priority` : null,
      riskFlags.damageCount ? `${riskFlags.damageCount} damage` : null,
      riskFlags.riskFactorCount ? `${riskFlags.riskFactorCount} risk factor${riskFlags.riskFactorCount > 1 ? "s" : ""}` : null,
    ].filter(Boolean).join(" · ");

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="inline-flex items-center gap-1.5 rounded-[6px] bg-green-100 px-2 py-1 text-[11px] font-semibold text-green-800">
          <span className="h-1.5 w-1.5 rounded-full bg-green-600" />
          Completed
        </span>
        <div className="flex items-center gap-3">
          {completedAt && (
            <span className="text-[11.5px] text-cl-muted">Generated {formatClearlineDateTime(completedAt)}</span>
          )}
          <button
            type="button"
            onClick={onRegenerate}
            className="text-[11.5px] font-semibold text-cl-accent hover:underline"
          >
            Regenerate
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <SummaryStatCard label="Property Type" value={formatEnumLabel(result.property_type)} />
        <SummaryStatCard
          label="Overall Condition"
          value={formatEnumLabel(result.overall_condition?.rating)}
          tone={CONDITION_TONE[result.overall_condition?.rating] || "neutral"}
        />
        <SummaryStatCard label="Occupancy" value={formatEnumLabel(result.occupancy?.status || result.occupancy?.occupancy_type)} />
        <SummaryStatCard label="Images Analysed" value={result.image_count ?? images?.length ?? null} />
        <SummaryStatCard
          label="Region Profile"
          value={result.region_profile?.name}
          sub={result.region_profile?.key}
        />
        <SummaryStatCard
          label="Risk Flags"
          value={riskFlags.total === 0 ? "None" : riskFlags.total}
          sub={riskSub}
          tone={riskFlags.total === 0 ? "good" : "bad"}
        />
      </div>

      {result.narrative && <p className="text-[13px] leading-relaxed text-cl-text">{result.narrative}</p>}
      {result.overall_condition?.explanation && (
        <p className="text-[12.5px] text-cl-muted">{result.overall_condition.explanation}</p>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-tile border border-cl-control p-4">
          <h3 className="mb-3 text-[13px] font-semibold text-cl-text">Construction</h3>
          <ConstructionPanel construction={result.construction} />
        </div>
        <div className="rounded-tile border border-cl-control p-4">
          <h3 className="mb-3 text-[13px] font-semibold text-cl-text">Systems</h3>
          <SystemsPanel systems={result.systems} images={images} mediaBaseUrl={mediaBaseUrl} />
        </div>
      </div>

      <RegionChecklistTable
        checklist={result.checklist}
        regionName={result.region_profile?.name}
        images={images}
        mediaBaseUrl={mediaBaseUrl}
      />

      <ImageInventoryStrip inventory={result.image_inventory} images={images} mediaBaseUrl={mediaBaseUrl} />

      {result.visible_damages?.length > 0 && (
        <div>
          <h3 className="mb-2 text-[13px] font-semibold text-cl-text">Visible Damages</h3>
          <div className="space-y-2">
            {result.visible_damages.map((d, i) => (
              <div key={i} className="rounded-tile border border-cl-control p-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[13px] font-medium text-cl-text">{formatEnumLabel(d.element)}</span>
                  <Chip label={d.severity} colors={SEVERITY_CHIPS[d.severity]} />
                </div>
                {d.description && <p className="mt-1 text-[11.5px] text-cl-muted">{d.description}</p>}
                <EvidenceThumbs indices={d.evidence_images} images={images} mediaBaseUrl={mediaBaseUrl} />
              </div>
            ))}
          </div>
        </div>
      )}

      {result.risk_factors?.length > 0 && (
        <div>
          <h3 className="mb-2 text-[13px] font-semibold text-cl-text">Risk Factors</h3>
          <div className="flex flex-wrap gap-2">
            {result.risk_factors.map((r, i) => (
              <span key={i} className="rounded-[6px] bg-cl-bg px-2 py-1 text-[11.5px] text-cl-text">
                {r.factor} <span className="text-cl-muted">· {formatEnumLabel(r.category)}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {result.protection?.length > 0 && (
        <div>
          <h3 className="mb-2 text-[13px] font-semibold text-cl-text">Protection Measures</h3>
          <div className="space-y-1">
            {result.protection.map((p, i) => (
              <div key={i} className="flex items-center justify-between text-[12.5px]">
                <span className="text-cl-text">{formatEnumLabel(p.measure)}</span>
                <Chip label={p.status} colors={p.status === "present" ? CONDITION_CHIPS.good : undefined} />
              </div>
            ))}
          </div>
        </div>
      )}

      {result.liability_hazards?.length > 0 && (
        <div>
          <h3 className="mb-2 text-[13px] font-semibold text-cl-text">Liability Hazards</h3>
          <ul className="list-inside list-disc space-y-1 text-[12.5px] text-cl-text">
            {result.liability_hazards.map((h, i) => (
              <li key={i}>
                {formatEnumLabel(h.hazard)}
                {h.description ? ` — ${h.description}` : ""}
              </li>
            ))}
          </ul>
        </div>
      )}

      {result.recommendations?.length > 0 && (
        <div>
          <h3 className="mb-2 text-[13px] font-semibold text-cl-text">Recommendations</h3>
          <ul className="list-inside list-disc space-y-1 text-[12.5px] text-cl-text">
            {result.recommendations.map((r, i) => (
              <li key={i}>{r}</li>
            ))}
          </ul>
        </div>
      )}

      {result.photo_coverage?.areas_not_covered?.length > 0 && (
        <p className="text-[11.5px] text-cl-muted">
          Not covered by photos: {result.photo_coverage.areas_not_covered.join(", ")}
        </p>
      )}

      {result.limitations?.length > 0 && (
        <p className="text-[11.5px] italic text-cl-muted">{result.limitations.join(" ")}</p>
      )}
    </div>
  );
}

export default function PropertyCaseDetailsRedesign() {
  const { caseId: _encodedCaseId } = useParams();
  const routerLocation = useLocation();
  const caseId = decodeRefNum(_encodedCaseId);

  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const aiSummary = usePropertyAiSummary();
  const report = usePropertyDownloadReport();
  const [aiExpanded, setAiExpanded] = useState(false);
  // Photographs used to be anchors with target="_blank", so a tap left the
  // portal for a raw image file in a new tab with no way back into the
  // case, no zoom, and no way to reach the next photograph. Same lightbox
  // the motor pages use.
  const [lightbox, setLightbox] = useState({ open: false, images: [], index: 0 });
  // ImageLightbox is mounted on this page at all times (it renders null
  // while closed, per its own isOpen check), and one of its own effects
  // depends on this callback's identity — so a fresh arrow function passed
  // inline here re-triggers that effect on every render of this page, which
  // sets this state again with a new object, which re-renders this page,
  // which passes another fresh function… "Maximum update depth exceeded"
  // logged hundreds of times a second, the moment the case page mounts,
  // whether or not the lightbox is ever opened. useCallback keeps this
  // identity stable across renders, so the effect only re-fires when the
  // index it actually cares about changes.
  const handleLightboxImageChange = useCallback((index) => {
    setLightbox((prev) => ({ ...prev, index }));
  }, []);
  // Guards the background kick-off so it fires once per case, not on every
  // render the status happens to still be "idle".
  const aiStartedForRef = useRef(null);

  const loadCase = useCallback(async (refNum) => {
    setLoading(true);
    setError(null);
    try {
      const [assessmentResult, vehicleResult] = await Promise.allSettled([
        getPropertyAssessmentDetail(refNum),
        getPropertyVehicle(refNum),
      ]);
      const assessmentData = assessmentResult.status === "fulfilled" ? assessmentResult.value : null;
      const vehicleData = vehicleResult.status === "fulfilled" ? vehicleResult.value : null;
      if (!assessmentData && !vehicleData) throw new Error("No data returned for this case");
      setOverview(toPropertyOverview(assessmentData, vehicleData));
    } catch (err) {
      console.error("Error loading property case:", err);
      setError("Failed to load this case");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (caseId) loadCase(caseId);
    aiSummary.reset();
    aiStartedForRef.current = null;
    setAiExpanded(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, loadCase]);

  // Unlike the old GET /get_summary wrapper, Property Insights has no notion
  // of this case at all — the photos themselves are fetched and submitted,
  // not looked up server-side from a ref_num. One place to build that
  // request so the kickoff effect, Try Again, and Regenerate can't drift.
  const runAiSummary = useCallback(() => {
    aiSummary.generate({
      photos: overview?.images,
      mediaBaseUrl: getPropertyMediaBaseUrl(),
      country: overview?.country || countryFromSearch(routerLocation.search),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overview, routerLocation.search]);

  // Kick the summary off in the background once the case's photos are known,
  // so it is already running (often finished) before anyone scrolls down to
  // it. Expanding the section only reveals the result, it does not start it.
  useEffect(() => {
    if (!overview) return;
    if (aiStartedForRef.current === caseId) return;
    if (!overview.images || overview.images.length === 0) return;
    aiStartedForRef.current = caseId;
    runAiSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overview, caseId]);

  /**
   * What Parkhi shows in his speech bubble while hovered on this page —
   * mirrors CaseDetailsRedesign.js's cammyPageSummary. Computed here, before
   * the loading/error early returns below, since hooks must run
   * unconditionally on every render; it duplicates a couple of small
   * derivations (status text, country) that displayCountry/statusText/chip
   * below also compute, rather than moving those — overview is null during
   * loading either way and every field here already falls back for that.
   */
  const cammyPageSummary = useMemo(() => {
    const summaryStatusText = getStatusInfo(parseInt(overview?.status, 10)).text;
    const summaryCountry =
      overview?.country || countryFromSearch(routerLocation.search);
    const title =
      overview?.address && overview.address !== "—"
        ? overview.address
        : overview?.refNum || caseId;
    const facts = [
      summaryCountry && { label: "Country", value: summaryCountry },
      overview?.images?.length > 0 && {
        label: "Images",
        value: overview.images.length,
      },
      overview?.timeTakenToUpload && {
        // Same wording as the motor redesign's header and this page's own
        // Inspection Details grid below — one concept, one label everywhere.
        label: "Time taken to upload",
        value: formatUploadDuration(overview.timeTakenToUpload),
      },
    ].filter(Boolean);
    return {
      title,
      status: summaryStatusText,
      statusChip: getClearlineStatusChip(summaryStatusText),
      facts,
    };
  }, [overview, caseId, routerLocation.search]);
  usePublishCammyPageSummary(cammyPageSummary);

  const pageShellClass ="flex h-full min-h-0 w-full flex-col overflow-y-auto bg-cl-bg px-4 py-4 sm:px-8 sm:py-[26px] font-clearline text-cl-text";
  const cardClass = "rounded-card border border-cl-card bg-cl-surface p-6";

  if (loading) {
    return (
      <div className={pageShellClass}>
        <p className="text-[13px] text-cl-muted">Loading case…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={pageShellClass}>
        <p className="mb-4 text-[14px] font-semibold text-cl-text">{error}</p>
        <Link
          to="/redesign/property-cases"
          className="inline-flex w-fit rounded-control bg-cl-accent px-4 py-[9px] text-[13px] font-semibold text-white shadow-cl-primary hover:bg-cl-accent-hover"
        >
          Back to Property Cases
        </Link>
      </div>
    );
  }

  const displayCountry = overview?.country || countryFromSearch(routerLocation.search);
  const statusText = getStatusInfo(parseInt(overview?.status, 10)).text;
  const chip = getClearlineStatusChip(statusText);
  const imageBuckets = groupPropertyImagesBySide(overview?.images);
  const mediaBaseUrl = getPropertyMediaBaseUrl();
  // A photo with no location permission still carries lat/long — as the
  // literal string "None", which `img?.lat && img?.long` reads as truthy —
  // so the Map rendered anyway, with every "None" plotted at (0, 0) and
  // nothing else to show for it. Same validity check as the adapter's own
  // noLocationPermission (toPropertyOverview), so the Map and the warning
  // agree on whether there's anything real to plot.
  const allImagesForMap = (overview?.images || [])
    .filter(
      (img) =>
        Number.isFinite(parseFloat(img?.lat)) &&
        Number.isFinite(parseFloat(img?.long)),
    )
    .map((img) => ({ ...img, longitude: img.long }));

  // Progress is surfaced in the collapsed header so the section reads as
  // "working on it" without having to be opened.
  const aiStatusLabel =
    aiSummary.status === "processing"
      ? aiSummary.progress
        ? `Analyzing… (${aiSummary.progress.batches_done ?? 0}/${aiSummary.progress.batches_total ?? "?"})`
        : "Analyzing…"
      : aiSummary.status === "failed"
        ? "Failed"
        : aiSummary.status === "completed"
          ? "Ready"
          : "";

  return (
    <div className={pageShellClass}>
      <Link
        to="/redesign/property-cases"
        className="mb-4 flex w-fit items-center gap-1 text-[12.5px] text-cl-muted hover:text-cl-secondary"
      >
        <ChevronLeft className="h-4 w-4" />
        Back to Property Cases
      </Link>

      {/* Title row — status left, the primary action right, same split and
          alignment as the motor redesign's header (status/warnings left,
          Approve right), kept outside the facts card below rather than
          sharing it. */}
      <div className="mb-[6px] flex flex-wrap items-center justify-between gap-x-6 gap-y-2">
        <span
          className="inline-flex flex-none items-center gap-[6px] rounded-pill px-[11px] py-1 text-[11.5px] font-semibold"
          style={{ background: chip.bg, color: chip.fg }}
        >
          <span className="h-[6px] w-[6px] rounded-pill" style={{ background: chip.fg }} />
          {statusText}
        </span>
        <button
          type="button"
          onClick={() => report.download(overview?.refNum || caseId)}
          disabled={report.status === "downloading"}
          className="flex items-center gap-1.5 rounded-tile border border-cl-card px-3 py-1.5 text-[11.5px] font-medium text-cl-muted transition-colors hover:bg-cl-hover hover:text-cl-text disabled:cursor-not-allowed disabled:opacity-60"
        >
          <Download className="h-3.5 w-3.5" />
          {report.status === "downloading" ? "Downloading…" : "Download Report"}
        </button>
      </div>
      {report.status === "error" && (
        <p className="mb-2 text-right text-[11.5px] text-red-600">{report.error}</p>
      )}

      {/* Facts card — same tile chrome and two-column, left-aligned, divider
          layout as the motor redesign's header (that one splits Make/Model/
          Inspection ID from Country/Time taken to upload/Received the same
          way). No photo — the address is just another fact, same as every
          other field here, not a name card. */}
      <div className={`${cardClass} mb-6 p-4`}>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-stretch">
          <div className="flex flex-1 flex-col gap-[10px]">
            <MetaItem
              icon={Building2}
              label="Property Address"
              value={overview?.address || "—"}
              valueClassName="break-words"
            />
            <MetaItem icon={Hash} label="Inspection ID" value={overview?.refNum || "—"} />
          </div>

          <div className="hidden w-px self-stretch bg-cl-card sm:block" />
          <div className="flex flex-1 flex-col gap-[10px]">
            {displayCountry && (
              <MetaItem
                icon={Globe}
                label="Country"
                value={displayCountry}
                trailing={
                  getCountryFlagSrc(displayCountry) && (
                    <img
                      alt=""
                      className="h-[11px] w-4 rounded-[2px] object-cover"
                      src={getCountryFlagSrc(displayCountry)}
                    />
                  )
                }
              />
            )}
            {overview?.timeTakenToUpload && (
              <MetaItem
                icon={Clock}
                label="Time taken to upload"
                value={formatUploadDuration(overview.timeTakenToUpload)}
              />
            )}
            {/* Same field (created_at) the motor redesign's header calls
                "Received", and what both Cases list pages' own RECEIVED
                column already call it — one name for one timestamp. */}
            <MetaItem
              icon={Inbox}
              label="Received"
              value={formatFullDateTime(overview?.createdAt)}
            />
          </div>
        </div>
      </div>

      {overview?.warnings?.length > 0 && (
        <div className="mb-6 flex gap-3 rounded-card border border-[#f5cf87] bg-[#fffaf0] p-4">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-[#b7791f]" />
          <div>
            <div className="mb-1 text-[13px] font-semibold text-[#8a5c07]">Warnings</div>
            {overview.warnings.map((w, i) => (
              <p key={i} className="text-[12.5px] text-[#8a5c07]">{w}</p>
            ))}
          </div>
        </div>
      )}

      <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
        <StatTile label="Uploaded Images" value={overview?.images?.length ?? 0} />
        <StatTile label="Exterior Images" value={imageBuckets.exterior.length} />
        <StatTile label="Interior Images" value={imageBuckets.interior.length} />
        <StatTile label="Additional Images" value={imageBuckets.additional.length} />
      </div>

      <div className={`${cardClass} mb-6`}>
        <h2 className="mb-4 text-[14px] font-semibold text-cl-text">Inspection Details</h2>
        <dl className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
          <DetailRow label="Ergo ID" value={overview?.ergoId} />
          <DetailRow label="Inspection Type" value={formatEnumLabel(overview?.inspectionType)} />
          <DetailRow label="Property Type" value={overview?.propertyType ? formatEnumLabel(overview.propertyType) : ""} />
          <DetailRow label="Time Taken to Upload" value={overview?.timeTakenToUpload} />
          <DetailRow label="Device" value={overview?.deviceMakeModel} />
          <DetailRow label="OS / Browser" value={overview?.osSummary} />
          <DetailRow label="Inspection Mode" value={overview?.inspectionMode ? formatEnumLabel(overview.inspectionMode) : ""} />
          <DetailRow
            label="Internet Speed Range"
            value={
              overview?.minInternetSpeed && overview?.maxInternetSpeed
                ? `${overview.minInternetSpeed} - ${overview.maxInternetSpeed} Mbps`
                : ""
            }
          />
        </dl>
      </div>

      <div className={`${cardClass} mb-6`}>
        <h2 className="mb-4 text-[14px] font-semibold text-cl-text">TriNetra Ratings</h2>
        {overview?.userRating != null ? (
          <div className="flex items-center gap-2">
            <span className="text-[13px] font-medium text-cl-text">Rating:</span>
            <StarRating rating={overview.userRating} />
          </div>
        ) : (
          <p className="text-[13px] text-cl-text">User did not provide rating.</p>
        )}
        {overview?.reviewerComment && (
          <p className="mt-2 text-[12.5px] text-cl-muted">
            <span className="font-medium text-cl-text">Comment: </span>
            {overview.reviewerComment}
          </p>
        )}
      </div>

      {IMAGE_SECTIONS.map(({ key, label }) => {
        const images = imageBuckets[key];
        if (!images || images.length === 0) return null;
        return (
          <div key={key} className={`${cardClass} mb-6`}>
            <h2 className="mb-4 text-[14px] font-semibold text-cl-text">{label}</h2>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {images.map((img, i) => {
                const src = buildPropertyImageUrl(mediaBaseUrl, img?.picture);
                if (!src) return null;
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() =>
                      setLightbox({
                        open: true,
                        index: i,
                        images: images
                          .map((candidate, j) => ({
                            id: `${key}-${j}`,
                            imageUrl: buildPropertyImageUrl(mediaBaseUrl, candidate?.picture),
                            title: candidate?.name ? formatEnumLabel(candidate.name) : label,
                          }))
                          .filter((entry) => entry.imageUrl),
                      })
                    }
                    className="block aspect-square overflow-hidden rounded-thumb bg-cl-bg"
                    title={img?.name ? formatEnumLabel(img.name) : undefined}
                  >
                    <img alt={img?.name || `${label} ${i + 1}`} className="h-full w-full object-cover" src={src} />
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}

      {allImagesForMap.length > 0 && (
        <div className={cardClass}>
          <h2 className="mb-4 text-[14px] font-semibold text-cl-text">Map</h2>
          <div className="h-[260px] sm:h-[420px] overflow-hidden rounded-thumb">
            <CustomMap
              uploaded={allImagesForMap}
              setMaxDistance={() => {}}
              showPolygon={false}
              setShowPolygon={() => {}}
            />
          </div>
        </div>
      )}

      {/* AI Summary — last on the page, and generated in the background as
          soon as the case loads so the result is usually ready by the time
          anyone scrolls here. The header button only expands/collapses it. */}
      <div className={`${cardClass} mt-6`}>
        <button
          type="button"
          onClick={() => setAiExpanded((v) => !v)}
          className="flex w-full items-center justify-between gap-3 text-left"
        >
          <h2 className="flex items-center gap-2 text-[14px] font-semibold text-cl-text">
            <Sparkles className="h-4 w-4 text-purple-500" />
            AI Property Summary
          </h2>
          <div className="flex items-center gap-3">
            <span className="text-[11.5px] text-cl-muted">{aiStatusLabel}</span>
            <ChevronDown
              className={`h-4 w-4 text-cl-faint transition-transform ${aiExpanded ? "rotate-180" : ""}`}
            />
          </div>
        </button>

        {aiExpanded && (
          <div className="mt-4">
            {aiSummary.status === "failed" && (
              <div>
                <p className="mb-3 text-[12.5px] text-[#c22f2f]">{aiSummary.error}</p>
                <button
                  type="button"
                  onClick={runAiSummary}
                  className="rounded-control bg-cl-accent px-4 py-[9px] text-[12.5px] font-semibold text-white shadow-cl-primary hover:bg-cl-accent-hover"
                >
                  Try Again
                </button>
              </div>
            )}

            {aiSummary.status === "completed" && aiSummary.result && (
              <AiSummaryResult
                result={aiSummary.result}
                images={aiSummary.images}
                completedAt={aiSummary.completedAt}
                mediaBaseUrl={mediaBaseUrl}
                onRegenerate={runAiSummary}
              />
            )}
          </div>
        )}
      </div>

      <ImageLightbox
        isOpen={lightbox.open}
        images={lightbox.images}
        currentIndex={lightbox.index}
        onImageChange={handleLightboxImageChange}
        onClose={() => setLightbox({ open: false, images: [], index: 0 })}
      />
    </div>
  );
}
